<template>
  <BookmarkCard :tag-type="tagType" icon-name="paper-plane">
    <template #content>
      <div v-if="project.etablissements[0]" class="PendingProjectCard-text">
        {{ project.etablissements[0].nom }}
      </div>
      <div class="PendingProjectCard-text">{{ project.natureAireEducative }}</div>
      <div class="PendingProjectCard-text name">{{ project.nom }}</div>
    </template>
    <template #footer>
      <div v-if="invitationStatus === 'pending'" class="buttons PendingProjectCard-buttons">
        <b-button type="is-danger" rounded @click="declineInvitation">{{ $t("refuser") }}</b-button>
        <b-button type="is-primary" rounded @click="acceptInvitation">{{ $t("valider") }}</b-button>
      </div>
      <div v-else-if="invitationStatus === 'accepted'" class="PendingProjectCard-mention">
        {{ $t("myProjects.accepted") }}
        <b-icon icon="check-circle" size="is-large" type="is-success" />
      </div>
      <div v-else class="PendingProjectCard-mention declined">
        {{ $t("myProjects.declined") }}
        <b-icon icon="times-circle" size="is-large" type="is-danger" />
      </div>
    </template>
  </BookmarkCard>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import BookmarkCard from "../generic_components/BookmarkCard.vue"
import { INVITATION_STATUS, TAG_TYPES_FOR_BOOKMARK_CARD } from "@/model/bean/EnumHelper"
import { ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import { ProjectService } from "@/services/rest/project/ProjectService"
import { BButton, BIcon } from "buefy"

export default defineComponent({
  name: "PendingProjectCard",
  components: {
    BookmarkCard,
    BButton,
    BIcon,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    loggedUserId: {
      type: String,
      default: "",
    },
    sendToServer: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["invitation-processed", "project-updated"],
  data() {
    return {
      tagType: TAG_TYPES_FOR_BOOKMARK_CARD.INVITATION,
      invitationStatus: INVITATION_STATUS.PENDING,
      projectService: ProjectService.INSTANCE,
    }
  },
  methods: {
    async acceptInvitation(): Promise<void> {
      this.invitationStatus = INVITATION_STATUS.ACCEPTED

      // Créer une copie des données pour éviter la mutation de props
      const updatedProject = { ...this.project }
      updatedProject.consentementEnAttente = [...this.project.consentementEnAttente]
      updatedProject.consentementValide = this.project.consentementValide
        ? [...this.project.consentementValide]
        : []

      // Modifier la copie
      updatedProject.consentementEnAttente.splice(
        updatedProject.consentementEnAttente.indexOf(this.loggedUserId),
        1,
      )
      updatedProject.consentementValide.push(this.loggedUserId)

      if (this.sendToServer) {
        await this.projectService.sendAnswerAboutPendingInvitation(
          updatedProject,
          this.loggedUserId,
        )
        this.$emit("invitation-processed")
      }

      // Émettre les modifications au parent
      this.$emit("project-updated", updatedProject)
    },

    async declineInvitation(): Promise<void> {
      this.invitationStatus = INVITATION_STATUS.DECLINED

      // Créer une copie des données pour éviter la mutation de props
      const updatedProject = { ...this.project }
      updatedProject.consentementEnAttente = [...this.project.consentementEnAttente]

      // Modifier la copie
      updatedProject.consentementEnAttente.splice(
        updatedProject.consentementEnAttente.indexOf(this.loggedUserId),
        1,
      )

      if (this.sendToServer) {
        await this.projectService.sendAnswerAboutPendingInvitation(
          updatedProject,
          this.loggedUserId,
        )
        this.$emit("invitation-processed")
      }

      // Émettre les modifications au parent
      this.$emit("project-updated", updatedProject)
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.PendingProjectCard-buttons {
  width: 100%;
  padding: 0 0.25rem;

  &.buttons {
    justify-content: flex-end;

    .button {
      min-width: 57px;
      text-transform: uppercase;
    }
  }
}

.PendingProjectCard-text {
  text-align: center;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  &.name {
    font-weight: 700;
  }
}

.PendingProjectCard-mention {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  font-style: italic;
  color: $success;
  width: 100%;
  padding: 0 0.25rem;

  &.declined {
    color: $danger;
  }
}
</style>
